import { GrantPanel } from '../components/GrantPanel';
import type { AgentTemplateWizardStepType, WizardDraft, WizardStepProps } from '../types';

type MemoryIntent = WizardDraft['memory'];

function isMemoryIntent(value: unknown): value is MemoryIntent {
  return Boolean(value && typeof value === 'object' && 'attach' in (value as MemoryIntent));
}

/**
 * `attach-storage` step (design §12): the switch defaults to
 * `template.memory.attachStorageMemory` (seeded in the draft); ON reveals the
 * STATIC grant panel — read/write is granted as a unit by the P2-T4 endpoint,
 * so no per-permission toggles are implied (audit #4). The toggle is an
 * INTENT, executed post-create (§17.3/§15.3). Optional step: Continue is never
 * blocked by it.
 */
function AttachStorageStep({ step, value, onChange }: WizardStepProps) {
  const memory: MemoryIntent = isMemoryIntent(value) ? value : { attach: false };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Give the agent a private memory collection it can read and write.
      </p>

      <div className="flex items-start gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={memory.attach}
          aria-label={step.title}
          onClick={() => onChange({ attach: !memory.attach })}
          className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors motion-reduce:transition-none ${
            memory.attach ? 'border-primary bg-primary' : 'border-input bg-input'
          }`}
        >
          <span
            aria-hidden="true"
            className={`block h-4 w-4 rounded-full bg-background transition-transform motion-reduce:transition-none ${
              memory.attach ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
        <div className="space-y-1">
          <p className="text-sm font-medium">{step.title}</p>
          {!memory.attach && (
            <p className="text-sm text-muted-foreground">
              The agent remembers context between runs.
            </p>
          )}
        </div>
      </div>

      {memory.attach ? <GrantPanel /> : null}
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'attach-storage';
export const component = AttachStorageStep;
